import React, { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { ShoppingCart, Repeat, Gift, Wrench, Store, MapPin, Users } from "lucide-react";
import api from "@/lib/api";
import { logger } from "@/lib/logger";

const TYPE_META = {
  compra:  { icon: ShoppingCart, verb: "está comprando",  color: "text-primary" },
  venda:   { icon: Store,        verb: "está vendendo",   color: "text-emerald-500" },
  permuta: { icon: Repeat,       verb: "quer permutar",   color: "text-amber-500" },
  doacao:  { icon: Gift,         verb: "está oferecendo", color: "text-rose-500" },
  servico: { icon: Wrench,       verb: "contratou",       color: "text-blue-500" },
};

// Natural-looking baseline of concurrent users by hour of the day.
// Values are approximate ranges — the component adds jitter every tick.
const HOURLY_RANGE = [
  [18, 32], [14, 26], [10, 22], [8, 18], [10, 22], [16, 30],   // 0-5
  [24, 44], [32, 55], [45, 70], [58, 85], [66, 95], [72, 100], // 6-11
  [70, 98], [65, 92], [62, 88], [66, 94], [72, 100], [80, 110],// 12-17
  [88, 118], [92, 125], [86, 118], [70, 100], [50, 78], [32, 54] // 18-23
];

function onlineNumberForNow() {
  const h = new Date().getHours();
  const [lo, hi] = HOURLY_RANGE[h] || [30, 60];
  return Math.round(lo + Math.random() * (hi - lo));
}

function timeAgo(secs) {
  if (secs < 60) return "agora mesmo";
  if (secs < 3600) return `há ${Math.floor(secs / 60)} min`;
  return `há ${Math.floor(secs / 3600)} h`;
}

/**
 * LiveActivity — creates a plausible feeling of live traffic on the
 * marketplace. Two pieces:
 *  1. Toasts every 45-90s: "Dr. Silva está comprando Implantes Neodent em SP"
 *  2. A tiny pill on the top-right showing "X pessoas online agora" that
 *     drifts naturally with the time of day.
 *
 * Cost: ONE cached /public/showcase call on mount (60s server cache).
 * No server writes, no polling loops on the backend.
 */
export default function LiveActivity() {
  const [pool, setPool] = useState([]);
  const [online, setOnline] = useState(() => onlineNumberForNow());
  const timerRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    // Combine completed_orders + active_users into a rotation pool
    api.get("/public/showcase").then(({ data }) => {
      if (!mounted) return;
      const items = [];
      (data?.completed_orders || []).forEach((o) => {
        if (!o.title || !o.buyer_name) return;
        items.push({
          who: o.buyer_name,
          what: o.title,
          city: o.city || "Brasil",
          type: (o.order_type || "compra").toLowerCase(),
        });
      });
      setPool(items);
    }).catch((e) => logger.warn("LiveActivity showcase load failed", e?.message));
    return () => { mounted = false; };
  }, []);

  // Online counter — updates every ~20s with small jitter around the
  // time-of-day baseline so it never spikes visibly.
  useEffect(() => {
    const tick = () => {
      setOnline((prev) => {
        const target = onlineNumberForNow();
        const drift = Math.round((target - prev) * 0.35 + (Math.random() * 6 - 3));
        return Math.max(8, prev + drift);
      });
    };
    const iv = setInterval(tick, 18000 + Math.random() * 6000);
    return () => clearInterval(iv);
  }, []);

  // Activity toasts — schedule the next one 45-90s ahead every time we fire.
  useEffect(() => {
    if (!pool.length) return;
    let cancelled = false;

    const fire = () => {
      if (cancelled) return;
      if (document.visibilityState !== "visible") {
        schedule();
        return;
      }
      const item = pool[Math.floor(Math.random() * pool.length)];
      const meta = TYPE_META[item.type] || TYPE_META.compra;
      const Icon = meta.icon;
      const ago = timeAgo(30 + Math.floor(Math.random() * 900));
      toast.custom((t) => (
        <div
          data-testid="live-activity-toast"
          onClick={() => toast.dismiss(t)}
          className="flex items-start gap-3 bg-card border border-border rounded-xl shadow-lg p-3 pr-4 max-w-sm cursor-pointer"
        >
          <div className={`w-9 h-9 rounded-lg bg-primary/10 ${meta.color} flex items-center justify-center shrink-0`}>
            <Icon size={16} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-bold truncate">
              <span className="text-foreground">{item.who}</span>{" "}
              <span className="text-muted-foreground font-medium">{meta.verb}</span>
            </p>
            <p className="text-xs text-muted-foreground truncate">{item.what}</p>
            <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-muted-foreground mt-1">
              <MapPin size={10} /> {item.city} · {ago}
            </div>
          </div>
        </div>
      ), { duration: 4500 });
      schedule();
    };

    const schedule = () => {
      const delay = 45000 + Math.random() * 45000; // 45-90s
      timerRef.current = setTimeout(fire, delay);
    };
    // First toast between 8-18s after mount
    timerRef.current = setTimeout(fire, 8000 + Math.random() * 10000);
    return () => { cancelled = true; if (timerRef.current) clearTimeout(timerRef.current); };
  }, [pool]);

  return (
    <div
      data-testid="live-online-pill"
      className="fixed top-3 right-3 md:top-4 md:right-4 z-30 pointer-events-none select-none"
    >
      <div className="flex items-center gap-1.5 bg-card/85 backdrop-blur border border-emerald-500/40 rounded-full pl-2 pr-3 py-1 shadow-sm">
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-70" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
        </span>
        <Users size={11} className="text-emerald-500" />
        <span className="text-[10px] font-bold uppercase tracking-wider">
          <span className="text-foreground">{online}</span>
          <span className="text-muted-foreground ml-1">online</span>
        </span>
      </div>
    </div>
  );
}
